import React, { useState, useRef, useEffect } from 'react';
import { Plus, Minus, AlertCircle, User, Phone, MapPin, Package, Truck, Edit2, Trash2, Copy, Check, ArrowRightLeft, BookmarkCheck, ScanLine, Printer } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { capitalizeFirst, playSuccessSound, playErrorSound, getPriceWithoutTVA, getTVAAmount, formatPrice, roundPrice, calcLineTotal } from '../../lib/utils';

// Check if V3 (TVA mode) is active
const isV3 = () => localStorage.getItem('app_version') === 'v3';

// TTS function to speak the price
const speakPrice = (price) => {
    try {
        if (!('speechSynthesis' in window)) return;
        window.speechSynthesis.cancel();
        const text = String(Math.round(price * 100) / 100);
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'ro-RO';
        utterance.rate = 1.1;
        utterance.volume = 1;
        window.speechSynthesis.speak(utterance);
    } catch (err) {
        console.error('TTS error:', err);
    }
};

// New Product Modal (for pallet scan)
export const NewProductModal = ({
    open,
    onOpenChange,
    productData,
    onProductDataChange,
    categories,
    onSubmit
}) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <AlertCircle size={20} className="text-orange-500" />
                        Produs Nou pe Palet
                    </DialogTitle>
                </DialogHeader>
                
                <div className="mt-4 space-y-4">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <p className="text-sm text-orange-700">
                            Produsul <strong>{productData.barcode}</strong> nu există pe palet.
                            Completează datele pentru a-l adăuga:
                        </p>
                    </div>

                    <div>
                        <Label>Nume Produs *</Label>
                        <Input
                            value={productData.name}
                            onChange={(e) => onProductDataChange({...productData, name: capitalizeFirst(e.target.value)})}
                            placeholder="Denumire produs"
                            className="mt-1"
                        />
                    </div>
                    
                    <div>
                        <Label>Categorie *</Label>
                        <select
                            value={productData.category}
                            onChange={(e) => onProductDataChange({...productData, category: e.target.value})}
                            className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-lg bg-white"
                        >
                            <option value="">Selectează categoria</option>
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <Label>Cantitate pe palet</Label>
                        <Input
                            type="number"
                            min="1"
                            value={productData.quantity}
                            onChange={(e) => onProductDataChange({...productData, quantity: e.target.value})}
                            className="mt-1"
                        />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="flex-1"
                        >
                            Anulează
                        </Button>
                        <Button
                            onClick={onSubmit}
                            className="flex-1 bg-orange-600 hover:bg-orange-700"
                        >
                            <Plus size={16} className="mr-2" />
                            Adaugă pe Palet
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Customer Selection Modal (after scan)
export const CustomerSelectModal = ({
    open,
    onOpenChange,
    scannedProduct,
    activeCustomers,
    selectedCustomerId,
    onSelectCustomer,
    targetCustomer,
    onClearTargetCustomer,
    newCustomerMode,
    onToggleNewCustomerMode,
    newCustomerData,
    onNewCustomerDataChange,
    sellPrice,
    onSellPriceChange,
    sellQty,
    onSellQtyChange,
    priceInputRef,
    onSubmit
}) => {
    if (!scannedProduct) return null;

    // Auto-submit when selecting existing customer with price filled
    const handleCustomerSelect = (customerId) => {
        onSelectCustomer(customerId);
        // If price is already filled, submit automatically
        if (sellPrice && parseFloat(sellPrice) > 0) {
            setTimeout(() => {
                onSubmit();
            }, 100);
        }
    };

    // Handle Enter key on price/qty fields
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && sellPrice && parseFloat(sellPrice) > 0 && selectedCustomerId) {
            e.preventDefault();
            onSubmit();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {targetCustomer ? `Adaugă la ${targetCustomer.customer_id}` : 'Plasează la Client'}
                    </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                    {/* Target customer banner - shows when adding to specific customer */}
                    {targetCustomer && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between">
                            <div>
                                <p className="text-emerald-800 font-semibold">{targetCustomer.customer_id}</p>
                                <p className="text-emerald-600 text-xs">
                                    {targetCustomer.name || 'Client'} • {targetCustomer.item_count || 0} produse în comandă
                                </p>
                            </div>
                            <button 
                                onClick={onClearTargetCustomer}
                                className="text-xs text-emerald-600 hover:text-emerald-800 underline"
                            >
                                Schimbă
                            </button>
                        </div>
                    )}

                    {/* Product info */}
                    <div className={`rounded-lg p-3 ${
                        scannedProduct.source === 'palet_direct' || scannedProduct.source === 'palet_nou'
                            ? 'bg-orange-50 border border-orange-200' 
                            : 'bg-violet-50 border border-violet-200'
                    }`}>
                        <p className={`font-semibold ${
                            scannedProduct.source === 'palet_direct' || scannedProduct.source === 'palet_nou' ? 'text-orange-900' : 'text-violet-900'
                        }`}>{scannedProduct.name}</p>
                        <p className={`text-sm ${
                            scannedProduct.source === 'palet_direct' || scannedProduct.source === 'palet_nou' ? 'text-orange-600' : 'text-violet-600'
                        }`}>
                            Din: {scannedProduct.source === 'magazin' ? 'Magazin' : 
                                  scannedProduct.source === 'palet_activ' ? 'Palet Activ' : 
                                  scannedProduct.source === 'palet_direct' ? 'Palet Direct' :
                                  scannedProduct.source === 'palet_nou' ? 'Palet Nou' :
                                  scannedProduct.source === 'depozit' ? 'Depozit' : scannedProduct.source}
                        </p>
                        <p className={`text-xs ${
                            scannedProduct.source === 'palet_direct' || scannedProduct.source === 'palet_nou' ? 'text-orange-500' : 'text-violet-500'
                        }`}>Disponibil: {scannedProduct.quantity || '∞'} buc</p>
                        
                        {/* Show store stock when adding from pallet */}
                        {(scannedProduct.source === 'palet_direct' || scannedProduct.source === 'palet_nou' || scannedProduct.source === 'palet_activ') && (
                            <div className="mt-2 pt-2 border-t border-orange-200">
                                <p className="text-xs text-zinc-600 flex items-center gap-1">
                                    <Package size={12} />
                                    Stoc în Magazin: <strong className={scannedProduct.storeStock > 0 ? 'text-emerald-600' : 'text-zinc-400'}>
                                        {scannedProduct.storeStock !== undefined ? `${scannedProduct.storeStock} buc` : 'Se verifică...'}
                                    </strong>
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Price and Quantity */}
                    <div className="flex gap-3">
                        <div className="flex-1">
                            <Label className="text-sm font-semibold">Preț vânzare (RON)</Label>
                            <Input
                                ref={priceInputRef}
                                type="number"
                                step="0.01"
                                value={sellPrice}
                                onChange={(e) => onSellPriceChange(e.target.value)}
                                onKeyDown={handleKeyPress}
                                placeholder="Preț"
                                className="mt-1 h-12 text-lg font-semibold text-center"
                                autoFocus
                            />
                        </div>
                        <div className="w-36">
                            <Label className="text-sm font-semibold">Cantitate</Label>
                            <div className="flex items-center gap-1 mt-1">
                                <button
                                    onClick={() => onSellQtyChange(Math.max(1, parseInt(sellQty) - 1).toString())}
                                    className="w-10 h-12 rounded-lg bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-zinc-700"
                                >
                                    <Minus size={18} />
                                </button>
                                <Input
                                    type="number"
                                    min="1"
                                    max={scannedProduct.quantity}
                                    value={sellQty}
                                    onChange={(e) => onSellQtyChange(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    className="w-12 h-12 text-lg font-semibold text-center px-1"
                                />
                                <button
                                    onClick={() => onSellQtyChange(Math.min(scannedProduct.quantity, parseInt(sellQty) + 1).toString())}
                                    className="w-10 h-12 rounded-lg bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-zinc-700"
                                >
                                    <Plus size={18} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Quick customer buttons - hide if targetCustomer is set */}
                    {activeCustomers.length > 0 && !newCustomerMode && !targetCustomer && (
                        <div>
                            <Label className="text-xs text-zinc-500">Clienți cu comenzi ({activeCustomers.length}):</Label>
                            <div className="flex flex-wrap gap-1.5 mt-2 max-h-40 overflow-y-auto p-1">
                                {[...activeCustomers].reverse().map(c => (
                                    <button
                                        key={c.customer_id}
                                        onClick={() => handleCustomerSelect(c.customer_id)}
                                        className={`px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                            selectedCustomerId === c.customer_id
                                                ? 'bg-violet-600 text-white'
                                                : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                                        }`}
                                    >
                                        {c.customer_id}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* New customer or select existing - hide if targetCustomer is set */}
                    {!targetCustomer && !newCustomerMode ? (
                        <div className="flex gap-2 items-end">
                            <div className="flex-1">
                                <Label className="text-xs text-zinc-500">ID client:</Label>
                                <Input
                                    value={selectedCustomerId}
                                    onChange={(e) => onSelectCustomer(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    placeholder="Ex: CLIENT123"
                                    className="mt-1 h-9"
                                />
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onToggleNewCustomerMode(true)}
                                className="border-dashed h-9 text-xs"
                            >
                                <Plus size={14} className="mr-1" />
                                Client Nou
                            </Button>
                        </div>
                    ) : !targetCustomer ? (
                        <div className="space-y-3 p-3 bg-zinc-50 rounded-lg">
                            <p className="font-medium text-sm text-zinc-700">Date client nou:</p>
                            <div>
                                <Label>ID Client *</Label>
                                <Input
                                    value={newCustomerData.customer_id}
                                    onChange={(e) => onNewCustomerDataChange({...newCustomerData, customer_id: e.target.value})}
                                    placeholder="Ex: 1234"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Nume</Label>
                                <Input
                                    value={newCustomerData.name}
                                    onChange={(e) => onNewCustomerDataChange({...newCustomerData, name: capitalizeFirst(e.target.value)})}
                                    placeholder="Nume client"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Telefon</Label>
                                <Input
                                    value={newCustomerData.phone}
                                    onChange={(e) => onNewCustomerDataChange({...newCustomerData, phone: e.target.value})}
                                    placeholder="Telefon"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Adresă</Label>
                                <Input
                                    value={newCustomerData.address}
                                    onChange={(e) => onNewCustomerDataChange({...newCustomerData, address: e.target.value})}
                                    placeholder="Adresă livrare"
                                    className="mt-1"
                                />
                            </div>
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    onToggleNewCustomerMode(false);
                                    onNewCustomerDataChange({ customer_id: '', name: '', phone: '', address: '' });
                                }}
                                className="w-full text-zinc-500"
                            >
                                Înapoi la selectare client
                            </Button>
                        </div>
                    ) : null}

                    {/* Action buttons */}
                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="flex-1"
                        >
                            Anulează
                        </Button>
                        <Button
                            onClick={onSubmit}
                            disabled={!selectedCustomerId && (!newCustomerMode || !newCustomerData.customer_id)}
                            className="flex-1 bg-violet-600 hover:bg-violet-700"
                        >
                            <Plus size={16} className="mr-2" />
                            {targetCustomer ? 'Adaugă (Enter)' : 'Adaugă'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Print Order function
const printOrder = (customer, items) => {
    const customerId = customer?.customer_id || 'Client';
    const customerName = customer?.name || '';
    const customerPhone = customer?.phone || '';
    const total = roundPrice(items.reduce((sum, i) => sum + calcLineTotal(i.quantity, i.unit_price), 0)).toFixed(2);
    const date = new Date().toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });

    // Build table rows
    let tableRows = '';
    let lineNum = 0;
    items.forEach((item) => {
        if (item.is_fee || item.source === 'taxa') return; // Skip fees for main table
        lineNum++;
        const lineTotal = calcLineTotal(item.quantity, item.unit_price).toFixed(2);
        const isGift = item.is_gift || item.unit_price === 0;
        tableRows += `
            <tr${isGift ? ' style="background: #fdf2f8;"' : ''}>
                <td style="padding: 3px 4px; border-bottom: 1px solid #ddd; text-align: center;">${lineNum}</td>
                <td style="padding: 3px 4px; border-bottom: 1px solid #ddd;">${isGift ? '🎁 ' : ''}${item.name}</td>
                <td style="padding: 3px 4px; border-bottom: 1px solid #ddd; text-align: center;">${item.barcode || '-'}</td>
                <td style="padding: 3px 4px; border-bottom: 1px solid #ddd; text-align: center;">${item.quantity}</td>
                <td style="padding: 3px 4px; border-bottom: 1px solid #ddd; text-align: right;">${isGift ? 'CADOU' : item.unit_price?.toFixed(2)}</td>
                <td style="padding: 3px 4px; border-bottom: 1px solid #ddd; text-align: right; font-weight: 500;">${isGift ? '0.00' : lineTotal}</td>
            </tr>
        `;
    });

    // Add fees row if any
    const fees = items.filter(i => i.is_fee || i.source === 'taxa');
    fees.forEach(fee => {
        const feeTotal = calcLineTotal(fee.quantity, fee.unit_price).toFixed(2);
        tableRows += `
            <tr style="background: #fffbeb;">
                <td style="padding: 3px 4px; border-bottom: 1px solid #ddd; text-align: center;">-</td>
                <td style="padding: 3px 4px; border-bottom: 1px solid #ddd;" colspan="4">🚚 Taxa Transport</td>
                <td style="padding: 3px 4px; border-bottom: 1px solid #ddd; text-align: right; font-weight: 500;">${feeTotal}</td>
            </tr>
        `;
    });

    const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <title>Comandă - ${customerId}</title>
            <style>
                @page { size: A4; margin: 10mm; }
                body {
                    font-family: Arial, sans-serif;
                    font-size: 10pt;
                    line-height: 1.2;
                    color: #333;
                    margin: 0;
                    padding: 10px;
                }
                .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 8px;
                    padding-bottom: 6px;
                    border-bottom: 2px solid #7c3aed;
                }
                .header h1 {
                    margin: 0;
                    font-size: 16pt;
                    color: #7c3aed;
                }
                .header .client-info {
                    text-align: right;
                }
                .header .client-id {
                    font-size: 14pt;
                    font-weight: bold;
                    color: #7c3aed;
                }
                .header .client-name {
                    font-size: 11pt;
                    color: #333;
                }
                .header .client-phone {
                    font-size: 10pt;
                    color: #059669;
                }
                .header .date {
                    color: #666;
                    font-size: 8pt;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-bottom: 10px;
                }
                th {
                    background: #7c3aed;
                    color: white;
                    padding: 4px 4px;
                    text-align: left;
                    font-weight: 600;
                    font-size: 9pt;
                }
                th:first-child { text-align: center; width: 25px; }
                th:nth-child(2) { width: auto; }
                th:nth-child(3) { text-align: center; width: 80px; }
                th:nth-child(4) { text-align: center; width: 35px; }
                th:nth-child(5), th:nth-child(6) { text-align: right; width: 50px; }
                .total-row {
                    background: #f8f4ff;
                    font-size: 11pt;
                }
                .total-row td {
                    padding: 8px 4px;
                    font-weight: bold;
                }
                .footer {
                    margin-top: 20px;
                    padding-top: 10px;
                    border-top: 1px solid #ddd;
                    display: flex;
                    justify-content: space-between;
                }
                .signature {
                    width: 180px;
                    text-align: center;
                }
                .signature-line {
                    border-top: 1px solid #333;
                    margin-top: 30px;
                    padding-top: 3px;
                    font-size: 9pt;
                }
                @media print {
                    body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <div>
                    <h1>COMANDĂ</h1>
                    <div class="date">${date}</div>
                </div>
                <div class="client-info">
                    <div class="client-id">#${customerId}</div>
                    ${customerName ? `<div class="client-name">${customerName}</div>` : ''}
                    ${customerPhone ? `<div class="client-phone">📞 ${customerPhone}</div>` : ''}
                </div>
            </div>
            
            <table>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Produs</th>
                        <th>Cod</th>
                        <th>Cant.</th>
                        <th>Preț</th>
                        <th>Total</th>
                    </tr>
                </thead>
                <tbody>
                    ${tableRows}
                    <tr class="total-row">
                        <td colspan="5" style="text-align: right;">TOTAL:</td>
                        <td style="text-align: right; color: #7c3aed;">${total} RON</td>
                    </tr>
                </tbody>
            </table>
            
            <div class="footer">
                <div class="signature">
                    <div class="signature-line">Semnătură client</div>
                </div>
                <div class="signature">
                    <div class="signature-line">Semnătură vânzător</div>
                </div>
            </div>
        </body>
        </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Auto-print after a short delay
    setTimeout(() => {
        printWindow.print();
    }, 250);
};

// View Order Modal
export const ViewOrderModal = ({
    open,
    onOpenChange,
    customer,
    items,
    onEditCustomer,
    onEditItem,
    onDeleteItem,
    onMoveItem,
    onCopy,
    onComplete,
    checkedItems,
    onToggleCheck,
    onScanCheck,
    onBulkDelete,
    onBulkMove,
    onBulkMoveToReservation
}) => {
    const [scanInput, setScanInput] = useState('');
    const [lastScannedId, setLastScannedId] = useState(null);
    const scanInputRef = useRef(null);
    const itemRefs = useRef({});
    
    // Focus scan input when modal opens
    useEffect(() => {
        if (open) {
            // Reset last scanned when modal opens
            setLastScannedId(null);
            
            // Focus with multiple attempts to ensure it works
            const focusInput = () => {
                if (scanInputRef.current) {
                    scanInputRef.current.focus();
                    scanInputRef.current.select();
                }
            };
            
            // Try immediately, then after delays
            setTimeout(focusInput, 100);
            setTimeout(focusInput, 300);
            setTimeout(focusInput, 500);
        }
    }, [open]);
    
    // Scroll to last scanned item
    useEffect(() => {
        if (lastScannedId && itemRefs.current[lastScannedId]) {
            itemRefs.current[lastScannedId].scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
            });
        }
    }, [lastScannedId]);
    
    if (!customer) return null;

    // Get products only (exclude fees - gifts ARE included for verification)
    const productItems = items.filter(item => !item.is_fee && item.source !== 'taxa');
    
    // Calculate fully checked items (where checkedCount >= quantity)
    const fullyCheckedItems = productItems.filter(item => {
        const checkedCount = checkedItems?.[item.id] || 0;
        return checkedCount >= (item.quantity || 1);
    });
    
    // Calculate total items to check and total checked
    const totalItemsToCheck = productItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
    const totalChecked = productItems.reduce((sum, item) => {
        const checkedCount = checkedItems?.[item.id] || 0;
        return sum + Math.min(checkedCount, item.quantity || 1);
    }, 0);
    
    const allFullyChecked = totalChecked >= totalItemsToCheck && totalItemsToCheck > 0;

    // Handle scan input - find and increment check for the item
    const handleScanCheckLocal = (barcode) => {
        if (!barcode.trim()) return;
        
        const normalizedBarcode = barcode.trim().toUpperCase();
        
        // Find item by barcode (case insensitive) that's not fully checked yet
        const foundItem = productItems.find(item => {
            if (item.barcode?.toUpperCase() !== normalizedBarcode) return false;
            const checkedCount = checkedItems?.[item.id] || 0;
            return checkedCount < (item.quantity || 1); // Still needs checking
        });
        
        if (foundItem && onScanCheck) {
            // Found and needs checking - play success, speak price, and focus
            playSuccessSound();
            // Speak the price
            if (foundItem.unit_price != null) {
                speakPrice(foundItem.unit_price);
            }
            onScanCheck(foundItem.id, foundItem.quantity || 1);
            setLastScannedId(foundItem.id);
            setScanInput('');
            scanInputRef.current?.focus();
        } else {
            // Item not found or already fully checked - play error
            playErrorSound();
            setScanInput('');
            scanInputRef.current?.focus();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh]">
                <DialogHeader>
                    <DialogTitle className="flex items-center justify-between" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <div className="flex flex-col">
                            <span>Comandă {customer.customer_id}</span>
                            {customer.name && (
                                <span className="text-sm font-normal text-zinc-500">{customer.name}</span>
                            )}
                            {customer.phone ? (
                                <span className="text-sm font-normal text-emerald-600 flex items-center gap-1">
                                    <Phone size={12} />
                                    {customer.phone}
                                </span>
                            ) : (
                                <span className="text-xs font-normal text-amber-600 flex items-center gap-1">
                                    <AlertCircle size={12} />
                                    Fără telefon - clientul nu poate accesa portalul
                                </span>
                            )}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onEditCustomer}
                            className={customer.phone ? "text-zinc-500 hover:text-violet-600" : "text-amber-600 hover:text-amber-700 border border-amber-300"}
                        >
                            <Edit2 size={16} className="mr-1" />
                            {customer.phone ? 'Editează' : 'Adaugă Telefon'}
                        </Button>
                    </DialogTitle>
                </DialogHeader>
                
                <div className="mt-4 space-y-4">
                    {/* Scan to check input */}
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <ScanLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <Input
                                ref={scanInputRef}
                                value={scanInput}
                                onChange={(e) => setScanInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleScanCheckLocal(scanInput);
                                    }
                                }}
                                placeholder="Scanează cod de bare pentru a bifa..."
                                className="pl-9 h-10"
                            />
                        </div>
                        <Button
                            variant="outline"
                            onClick={() => handleScanCheckLocal(scanInput)}
                            className="h-10"
                        >
                            Bifează
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => {
                                // Check all items
                                productItems.forEach(item => {
                                    if (onScanCheck) {
                                        onScanCheck(item.id, item.quantity || 1, true); // true = set to full quantity
                                    }
                                });
                            }}
                            className="h-10 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                        >
                            <Check size={16} className="mr-1" />
                            Toate
                        </Button>
                    </div>
                    
                    {/* Progress indicator */}
                    {productItems.length > 0 && (
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                            allFullyChecked 
                                ? 'bg-green-100 text-green-700 border border-green-300' 
                                : 'bg-zinc-100 text-zinc-600'
                        }`}>
                            <Check size={16} className={allFullyChecked ? 'text-green-600' : 'text-zinc-400'} />
                            <span>Verificate: {totalChecked} / {totalItemsToCheck}</span>
                            {allFullyChecked && <span className="ml-2">✓ Complet!</span>}
                        </div>
                    )}

                    {/* Bulk Actions Bar - shows when items are checked */}
                    {fullyCheckedItems.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <span className="text-sm font-medium text-green-700">
                                {fullyCheckedItems.length} produse complete:
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onBulkMove && onBulkMove(fullyCheckedItems)}
                                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                            >
                                <ArrowRightLeft size={14} className="mr-1" />
                                Mută la alt client
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onBulkMoveToReservation && onBulkMoveToReservation(fullyCheckedItems)}
                                className="border-amber-300 text-amber-600 hover:bg-amber-50"
                            >
                                <BookmarkCheck size={14} className="mr-1" />
                                Mută la Rezervări
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onBulkDelete && onBulkDelete(fullyCheckedItems)}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                                <Trash2 size={14} className="mr-1" />
                                Șterge (în magazin)
                            </Button>
                        </div>
                    )}

                    {/* Customer info */}
                    {(customer.name || customer.phone || customer.address) && (
                        <div className="bg-zinc-50 rounded-lg p-3 space-y-1">
                            {customer.name && (
                                <p className="flex items-center gap-2 text-sm">
                                    <User size={14} className="text-zinc-400" />
                                    {customer.name}
                                </p>
                            )}
                            {customer.phone && (
                                <p className="flex items-center gap-2 text-sm">
                                    <Phone size={14} className="text-zinc-400" />
                                    {customer.phone}
                                </p>
                            )}
                            {customer.address && (
                                <p className="flex items-center gap-2 text-sm">
                                    <MapPin size={14} className="text-zinc-400" />
                                    {customer.address}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Items list */}
                    {items.length === 0 ? (
                        <div className="text-center py-6 text-zinc-500">
                            <Package size={32} className="mx-auto mb-2 text-zinc-300" />
                            <p>Niciun produs în comandă</p>
                        </div>
                    ) : (
                        <div className="max-h-[55vh] overflow-y-auto space-y-1">
                            {items.map((item, idx) => {
                                const isFee = item.is_fee || item.source === 'taxa';
                                const isGift = item.is_gift || item.source === 'gift';
                                const lineNum = isFee ? null : items.filter((i, j) => j < idx && !(i.is_fee || i.source === 'taxa')).length + 1;
                                const checkedCount = checkedItems?.[item.id] || 0;
                                const itemQty = item.quantity || 1;
                                const isFullyChecked = checkedCount >= itemQty;
                                const isPartiallyChecked = checkedCount > 0 && checkedCount < itemQty;
                                const isLastScanned = item.id === lastScannedId;
                                return (
                                    <div
                                        key={item.id}
                                        ref={el => itemRefs.current[item.id] = el}
                                        className={`flex items-center justify-between px-2 py-1.5 rounded-lg transition-all duration-300 ${
                                            isGift
                                                ? 'bg-pink-50 border border-pink-300'
                                                : isFee 
                                                ? 'bg-amber-50 border border-amber-200' 
                                                : isFullyChecked
                                                    ? 'bg-green-50 border border-green-300'
                                                    : isPartiallyChecked
                                                        ? 'bg-yellow-50 border border-yellow-300'
                                                        : 'bg-white border border-zinc-200'
                                        } ${isLastScanned && !isFee ? 'ring-2 ring-violet-400 ring-offset-1' : ''}`}
                                    >
                                        {/* Checkbox with progress - not shown for fees only (gifts have checkbox) */}
                                        {!isFee && onToggleCheck && (
                                            <button
                                                onClick={() => {
                                                    // Speak price when checking (not when already fully checked)
                                                    if (!isFullyChecked && item.unit_price != null) {
                                                        speakPrice(item.unit_price);
                                                    }
                                                    onToggleCheck(item.id, itemQty);
                                                }}
                                                className={`min-w-[2.5rem] h-6 rounded border-2 flex items-center justify-center mr-2 shrink-0 transition-colors text-xs font-bold ${
                                                    isFullyChecked 
                                                        ? 'bg-green-500 border-green-500 text-white' 
                                                        : isPartiallyChecked
                                                            ? 'bg-yellow-400 border-yellow-400 text-yellow-900'
                                                            : 'border-zinc-300 hover:border-green-400 text-zinc-400'
                                                }`}
                                            >
                                                {isFullyChecked ? (
                                                    <Check size={14} />
                                                ) : itemQty > 1 ? (
                                                    `${checkedCount}/${itemQty}`
                                                ) : null}
                                            </button>
                                        )}
                                        
                                        <div className="flex-1 flex items-center gap-2 min-w-0">
                                            {!isFee && !isGift && !onToggleCheck && (
                                                <span className="w-5 h-5 rounded-full bg-zinc-100 text-zinc-500 text-xs font-medium flex items-center justify-center shrink-0">
                                                    {lineNum}
                                                </span>
                                            )}
                                            {isFee && <Truck size={14} className="text-amber-600 shrink-0" />}
                                            {isGift && <span className="text-pink-500 shrink-0">🎁</span>}
                                            <div className="min-w-0 flex-1">
                                                <p className={`font-medium text-sm truncate ${
                                                    isGift ? 'text-pink-700' : isFee ? 'text-amber-800' : isFullyChecked ? 'text-green-800 line-through' : 'text-zinc-900'
                                                }`}>
                                                    {!isFee && !isGift && <span className="text-zinc-400 mr-1">{lineNum}.</span>}
                                                    {isGift && <span className="font-bold text-pink-600 mr-1">CADOU:</span>}
                                                    {item.name}
                                                </p>
                                                <div className="flex items-center gap-2 text-xs text-zinc-500">
                                                    {isGift ? (
                                                        <span className="text-pink-500 font-medium">Gratuit ({item.quantity} buc)</span>
                                                    ) : (
                                                        <span>{item.quantity} x {formatPrice(item.unit_price)} RON</span>
                                                    )}
                                                    {item.reserved_at && (
                                                        <span className="text-amber-600" title="Data rezervare">
                                                            (rez: {new Date(item.reserved_at).toLocaleDateString('ro-RO')})
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 ml-2">
                                            {!isFee && !isGift && item.barcode && (
                                                <span className="text-xs text-zinc-400 font-mono hidden sm:inline">{item.barcode}</span>
                                            )}
                                            {isGift ? (
                                                <span className="font-bold text-sm text-pink-500">GRATUIT</span>
                                            ) : (
                                                <span className={`font-bold text-sm ${isFee ? 'text-amber-600' : 'text-violet-600'}`}>
                                                    {formatPrice(item.quantity * item.unit_price)}
                                                </span>
                                            )}
                                            {!isFee && !isGift && (
                                                <button
                                                    onClick={() => onEditItem(item)}
                                                    className="p-1 text-zinc-400 hover:text-violet-600 hover:bg-violet-50 rounded"
                                                    title="Editează"
                                                >
                                                    <Edit2 size={12} />
                                                </button>
                                            )}
                                            {!isFee && !isGift && onMoveItem && (
                                                <button
                                                    onClick={() => onMoveItem(item)}
                                                    className="p-1 text-zinc-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                                    title="Mută"
                                                >
                                                    <ArrowRightLeft size={12} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => onDeleteItem(item)}
                                                className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded"
                                                title={isGift ? 'Șterge cadoul' : isFee ? 'Anulează taxa' : 'Șterge'}
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Total */}
                    <div className="flex flex-col gap-1 p-3 bg-violet-50 rounded-lg">
                        <div className="flex justify-between items-center">
                            <span className="font-semibold text-violet-900">TOTAL:</span>
                            <span className="text-xl font-bold text-violet-600">
                                {formatPrice(roundPrice(items.reduce((sum, i) => sum + calcLineTotal(i.quantity, i.unit_price), 0)))} RON
                            </span>
                        </div>
                        {isV3() && items.length > 0 && (
                            <div className="flex justify-between items-center text-sm border-t border-violet-200 pt-1 mt-1">
                                <span className="text-violet-600">Fără TVA: {formatPrice(getPriceWithoutTVA(roundPrice(items.reduce((sum, i) => sum + calcLineTotal(i.quantity, i.unit_price), 0))))}</span>
                                <span className="text-blue-600 font-medium">TVA (21%): {formatPrice(getTVAAmount(roundPrice(items.reduce((sum, i) => sum + calcLineTotal(i.quantity, i.unit_price), 0))))}</span>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => printOrder(customer, items)}
                            className="text-zinc-600 border-zinc-300 hover:bg-zinc-50"
                        >
                            <Printer size={16} className="mr-1" />
                            Print
                        </Button>
                        <Button
                            variant="outline"
                            onClick={onCopy}
                            className="flex-1"
                        >
                            <Copy size={16} className="mr-2" />
                            Copiază
                        </Button>
                        <Button
                            onClick={onComplete}
                            disabled={items.length === 0}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        >
                            <Check size={16} className="mr-2" />
                            Finalizează
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Edit Item Modal
export const EditItemModal = ({
    open,
    onOpenChange,
    item,
    editQty,
    onEditQtyChange,
    editPrice,
    onEditPriceChange,
    onSave
}) => {
    if (!item) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Editează Produs
                    </DialogTitle>
                </DialogHeader>
                
                <div className="mt-4 space-y-4">
                    <div className="bg-zinc-50 rounded-lg p-3">
                        <p className="font-medium text-zinc-900">{item.name}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Cantitate</Label>
                            <Input
                                type="number"
                                min="1"
                                value={editQty}
                                onChange={(e) => onEditQtyChange(e.target.value)}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label>Preț (RON)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={editPrice}
                                onChange={(e) => onEditPriceChange(e.target.value)}
                                className="mt-1"
                            />
                        </div>
                    </div>
                    
                    <div className="flex gap-3">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="flex-1"
                        >
                            Anulează
                        </Button>
                        <Button
                            onClick={onSave}
                            className="flex-1 bg-violet-600 hover:bg-violet-700"
                        >
                            Salvează
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Edit Customer Modal
export const EditCustomerModal = ({
    open,
    onOpenChange,
    customerData,
    onCustomerDataChange,
    onSave
}) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Editează Client {customerData.customer_id}
                    </DialogTitle>
                </DialogHeader>
                
                <div className="mt-4 space-y-4">
                    <div>
                        <Label>Nume</Label>
                        <Input
                            value={customerData.name}
                            onChange={(e) => onCustomerDataChange({...customerData, name: capitalizeFirst(e.target.value)})}
                            placeholder="Nume client"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label>Telefon</Label>
                        <Input
                            value={customerData.phone}
                            onChange={(e) => onCustomerDataChange({...customerData, phone: e.target.value})}
                            placeholder="Telefon"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label>Email</Label>
                        <Input
                            type="email"
                            value={customerData.email || ''}
                            onChange={(e) => onCustomerDataChange({...customerData, email: e.target.value})}
                            placeholder="email@exemplu.ro"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label>Adresă</Label>
                        <Input
                            value={customerData.address}
                            onChange={(e) => onCustomerDataChange({...customerData, address: e.target.value})}
                            placeholder="Adresă livrare"
                            className="mt-1"
                        />
                    </div>
                    
                    <div className="flex gap-3">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="flex-1"
                        >
                            Anulează
                        </Button>
                        <Button
                            onClick={onSave}
                            className="flex-1 bg-violet-600 hover:bg-violet-700"
                        >
                            Salvează
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};


// Move Item Modal - select target customer
export const MoveItemModal = ({
    open,
    onOpenChange,
    item,
    activeCustomers,
    currentCustomerId,
    onMove,
    onMoveToReservation
}) => {
    const [targetCustomerId, setTargetCustomerId] = React.useState('');
    const [newCustomerId, setNewCustomerId] = React.useState('');
    
    if (!item) return null;
    
    // Filter out current customer
    const otherCustomers = activeCustomers.filter(c => c.customer_id !== currentCustomerId);
    
    const handleMove = () => {
        const targetId = targetCustomerId || newCustomerId;
        if (!targetId) return;
        onMove(item, targetId);
        setTargetCustomerId('');
        setNewCustomerId('');
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Mută la alt client
                    </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                    {/* Item info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="font-semibold text-blue-900">{item.name}</p>
                        <p className="text-sm text-blue-600">
                            {item.quantity} x {formatPrice(item.unit_price)} RON = {formatPrice(item.quantity * item.unit_price)} RON
                        </p>
                        <p className="text-xs text-blue-500 mt-1">
                            De la: {currentCustomerId}
                        </p>
                    </div>

                    {/* Select existing customer */}
                    {otherCustomers.length > 0 && (
                        <div>
                            <Label className="text-sm text-zinc-600">Selectează client existent:</Label>
                            <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto p-1">
                                {otherCustomers.map(c => (
                                    <button
                                        key={c.customer_id}
                                        onClick={() => {
                                            setTargetCustomerId(c.customer_id);
                                            setNewCustomerId('');
                                        }}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                            targetCustomerId === c.customer_id
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                                        }`}
                                    >
                                        {c.customer_id}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Or enter new customer ID */}
                    <div>
                        <Label className="text-sm text-zinc-600">Sau introdu ID client nou:</Label>
                        <Input
                            value={newCustomerId}
                            onChange={(e) => {
                                setNewCustomerId(e.target.value);
                                setTargetCustomerId('');
                            }}
                            placeholder="Ex: CLIENT123"
                            className="mt-1"
                        />
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="flex-1"
                        >
                            Anulează
                        </Button>
                        <Button
                            onClick={handleMove}
                            disabled={!targetCustomerId && !newCustomerId}
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                        >
                            <ArrowRightLeft size={16} className="mr-2" />
                            Mută
                        </Button>
                    </div>
                    
                    {/* Move to reservations */}
                    <div className="border-t pt-3 mt-2">
                        <Button
                            variant="outline"
                            onClick={() => onMoveToReservation && onMoveToReservation(item)}
                            className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                        >
                            <BookmarkCheck size={16} className="mr-2" />
                            Mută la Rezervări ({currentCustomerId})
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
